import { CameraDirection, letterForCameraTag, positionForCameraTag } from "./cameraTagPosition";

export interface IResultSet {
  src: string;
  thumbnail?: string;
  idx: number;
  imageType?: string | null;
  tag?: string;
  sortNumber?: number | null;
  sortLetter?: string | null;
  tagPosition?: number | null;
  tagLetter?: string;
}

/** Pull the tag abbreviation and optional sub-letter out of a filename like
 *  "..._external_LF-a.jpg" or "..._external_LF-a_thumbnail.jpg". */
const URL_TAG_PATTERN = /_([A-Z]+)(?:-([a-z]+))?(?=(?:_thumbnail)?\.[a-zA-Z]+$)/;

const tagAndLetterFromUrl = (url: string | undefined): { tag: string | null; letter: string } => {
  if (!url) return { tag: null, letter: "" };
  const parts = url.split("/");
  const filename = (parts[parts.length - 1] || "").split("?")[0];
  const match = filename.match(URL_TAG_PATTERN);
  if (!match) return { tag: null, letter: "" };
  return { tag: match[1], letter: (match[2] || "").toLowerCase() };
};

/** Sort and enrich incoming image data.
 *  For exterior-style image types we use the tag-derived position
 *  (LF=1…F=8, T=9, with CW/CCW determined by `cameraDirection`).
 *  All other image types keep the legacy filename-based sort. */
export function buildSortedImages(
  images: any[],
  cameraDirection: CameraDirection = "cw",
): IResultSet[] {
  const enriched = images.map((image, idx) => {
    const src = image.imageUrlCf;
    const thumbnail = src ? src.replace(/(\.[^./\?]+)$/, '_thumbnail.jpg') : '';
    const { sortNumber, sortLetter, imageType } = extractSortDetails(src || "");
    // Position + sub-letter come from the filename (which always carries them),
    // with imageTag as a secondary fallback for older / non-standard names.
    const urlMeta = tagAndLetterFromUrl(src);
    const tagPosition =
      positionForCameraTag(urlMeta.tag, cameraDirection) ??
      positionForCameraTag(image.imageTag, cameraDirection);
    const tagLetter = urlMeta.letter || letterForCameraTag(image.imageTag);
    return { src, idx, tag: image.imageTag, sortNumber, sortLetter, imageType, thumbnail, tagPosition, tagLetter };
  });

  const imageTypeOrder: string[] = [];
  for (const img of enriched) {
    if (img.imageType && !imageTypeOrder.includes(img.imageType)) {
      imageTypeOrder.push(img.imageType);
    }
  }

  return imageTypeOrder.flatMap((type) => {
    const group = enriched.filter(img => img.imageType === type);
    const useTagSort = group.some(img => img.tagPosition != null);
    if (useTagSort) {
      return [...group].sort((a, b) => {
        const posDiff = (a.tagPosition ?? 9999) - (b.tagPosition ?? 9999);
        if (posDiff !== 0) return posDiff;
        return (a.tagLetter || "").localeCompare(b.tagLetter || "");
      });
    }
    return [...group].sort((a, b) => {
      if ((a.sortNumber ?? 999) !== (b.sortNumber ?? 999)) {
        return (a.sortNumber ?? 999) - (b.sortNumber ?? 999);
      }
      return (a.sortLetter || "").localeCompare(b.sortLetter || "");
    });
  });
}

/** Return unique image types from an array */
export function getUniqueImageTypes(images: IResultSet[]): string[] {
  return Array.from(
    new Set(
      images
        .map((i) => i.imageType)
        .filter((t): t is string => Boolean(t))
    )
  );
}

/** Check if WebGL is supported */
export function isWebGLSupported(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")));
  } catch {
    return false;
  }
}

/** Detect if running inside iframe mode */
export function isIframe(): boolean {
  return window.location.pathname.toLowerCase().includes("/iframe/");
}

/** Copy the current URL to clipboard safely */
export async function copyUrl(setCopied: (val: boolean) => void) {
  try {
    const url = window.location.href;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  } catch (err) {
    console.error("Failed to copy:", err);
  }
}

export const extractSortDetails = (
    imageUrl: string
): {
    sortNumber: number | null;
    sortLetter: string | null;
    imageType: string | null;
} => {
    if (!imageUrl) return { sortNumber: null, sortLetter: null, imageType: null };

    const parts = imageUrl.split("/");
    const filename = parts[parts.length - 1] || "";
    const folderName = parts[parts.length - 2] || "";

    // 🧠 Default imageType derived from folder name
    let imageType = folderName || null;

    const filenameParts = filename.split("-");
    const sortNumber = parseInt(filenameParts[1]);
    const sortLetter = filenameParts[2] || null;

    return { sortNumber, sortLetter, imageType };
};
